/**
 * R132: AudioManager — Howler.js facade for AIWARI.
 *
 * Three buses with independent volumes (master/music/sfx/ambient) plus a
 * sample-based SFX layer (Kenney CC0 packs in /public/audio/sfx/) and a
 * synth-based fallback (SfxEngine.ts) when no sample is mapped.
 *
 * Edgart arrays: any SFX_FILES entry that's a string[] gets a random pick
 * per call — used for footsteps, sword-clash, dagger-slice etc to avoid the
 * "machine-gun same wav" feel.
 *
 * Helper API for triggers:
 *   audio.playMelee(weaponSubtype)     — sword/axe/dagger/mace/hammer/polearm/fist
 *   audio.playRanged(kind)             — bow-draw|bow-shoot|arrow-hit|crossbow
 *   audio.playCast(school)             — fire|frost|holy|shadow|nature|arcane|lightning|cast-generic
 *   audio.playImpact(school)           — same schools, on-hit
 *   audio.playFootstep(surface)        — grass|stone|wood|snow|carpet
 *   audio.playDefense(kind)            — dodge|block|parry|immune|miss
 *   audio.playPain(severity, gender)   — light|heavy|crit
 *   audio.playDeath(gender)            — male|female (creature/player)
 *   audio.playLoot(kind)               — coin|item|armor|weapon
 *   audio.playEquip(slot)              — weapon|armor|cloth|leather
 *   audio.playUI(action)               — click|hover|tab|back|close|confirm|error
 *   audio.playWorld(kind)              — door-open|door-close|book-open|book-close|chop
 *   audio.playNotify(kind)             — quest-accept|quest-complete|level-up
 */
import { Howl, Howler } from 'howler';
import { playSfx as playSynth, type SfxName as SynthName } from './SfxEngine.js';

const BASE = `${import.meta.env.BASE_URL}audio/`;

// ─────────────────────────── SFX catalogue ───────────────────────────────

export type SfxId =
  // Legacy (existing MP3s)
  | 'click' | 'hover' | 'levelup' | 'quest_accept' | 'quest_complete' | 'gold_pickup'
  | 'sword_clang' | 'sword_swing' | 'dagger_stab' | 'axe_chop' | 'hammer_thud'
  | 'bow_draw' | 'bow_release' | 'arrow_impact'
  | 'spell_fire' | 'spell_frost' | 'spell_holy' | 'spell_shadow'
  | 'damage_male' | 'damage_female' | 'death_male' | 'death_female'
  | 'equip_armor' | 'equip_weapon' | 'unequip'
  | 'footstep_grass' | 'footstep_stone' | 'footstep_wood'
  // Melee — Kenney CC0
  | 'melee-sword-1h' | 'melee-sword-2h' | 'melee-sword-clash'
  | 'melee-axe-1h' | 'melee-axe-2h' | 'melee-mace-1h' | 'melee-hammer-2h' | 'melee-polearm'
  | 'melee-dagger' | 'melee-fist-light' | 'melee-fist-heavy' | 'melee-whoosh'
  // Defense
  | 'def-dodge' | 'def-block' | 'def-parry' | 'def-armor-hit' | 'def-immune'
  // Footsteps
  | 'foot-grass' | 'foot-stone' | 'foot-wood' | 'foot-snow' | 'foot-carpet'
  // Equip
  | 'equip-draw-blade' | 'equip-sheath-blade' | 'equip-cloth' | 'equip-leather'
  | 'equip-leather-belt' | 'equip-leather-drop'
  // Loot
  | 'loot-coins' | 'loot-pickup-item' | 'loot-pickup-armor' | 'loot-pickup-weapon'
  // World
  | 'world-door-open' | 'world-door-close' | 'world-book-open' | 'world-book-close'
  | 'world-book-flip' | 'world-creak' | 'world-metal-click' | 'world-chop'
  // UI
  | 'ui-click' | 'ui-back' | 'ui-close' | 'ui-close-window' | 'ui-confirm' | 'ui-error'
  | 'ui-cant-do' | 'ui-hover' | 'ui-tab'
  // Notify
  | 'notify-quest-accept' | 'notify-quest-complete' | 'notify-level-up-bell'
  // Spells (impact only — casts are synthesised)
  | 'spell-frost-impact' | 'spell-ice-shatter';

type Path = string | string[];

const SFX_FILES: Record<SfxId, Path> = {
  // Legacy MP3s
  click:           'sfx/ui/click.mp3',
  hover:           'sfx/ui/hover.mp3',
  levelup:         'sfx/ui/levelup.mp3',
  quest_accept:    'sfx/ui/quest_accept.mp3',
  quest_complete:  'sfx/ui/quest_complete.mp3',
  gold_pickup:     'sfx/ui/gold_pickup.mp3',
  sword_clang:     'sfx/combat/sword_clang.mp3',
  sword_swing:     'sfx/combat/sword_swing.mp3',
  dagger_stab:     'sfx/combat/dagger_stab.mp3',
  axe_chop:        'sfx/combat/axe_chop.mp3',
  hammer_thud:     'sfx/combat/hammer_thud.mp3',
  bow_draw:        'sfx/combat/bow_draw.mp3',
  bow_release:     'sfx/combat/bow_release.mp3',
  arrow_impact:    'sfx/combat/arrow_impact.mp3',
  spell_fire:      'sfx/spells/fire.mp3',
  spell_frost:     'sfx/spells/frost.mp3',
  spell_holy:      'sfx/spells/holy.mp3',
  spell_shadow:    'sfx/spells/shadow.mp3',
  damage_male:     'sfx/voice/damage_male.mp3',
  damage_female:   'sfx/voice/damage_female.mp3',
  death_male:      'sfx/voice/death_male.mp3',
  death_female:    'sfx/voice/death_female.mp3',
  equip_armor:     'sfx/equip/armor.mp3',
  equip_weapon:    'sfx/equip/weapon.mp3',
  unequip:         'sfx/equip/unequip.mp3',
  footstep_grass:  'sfx/foot/grass.mp3',
  footstep_stone:  'sfx/foot/stone.mp3',
  footstep_wood:   'sfx/foot/wood.mp3',

  // Kenney melee
  'melee-sword-1h':    'sfx/melee/sword-1h.ogg',
  'melee-sword-2h':    'sfx/melee/sword-2h.ogg',
  'melee-sword-clash': 'sfx/melee/sword-clash.ogg',
  'melee-axe-1h':      'sfx/melee/axe-1h.ogg',
  'melee-axe-2h':      'sfx/melee/axe-2h.ogg',
  'melee-mace-1h':     'sfx/melee/mace-1h.ogg',
  'melee-hammer-2h':   'sfx/melee/hammer-2h.ogg',
  'melee-polearm':     'sfx/melee/polearm.ogg',
  'melee-dagger':      ['sfx/melee/dagger-1.ogg', 'sfx/melee/dagger-2.ogg'],
  'melee-fist-light':  'sfx/melee/fist-light.ogg',
  'melee-fist-heavy':  'sfx/melee/fist-heavy.ogg',
  'melee-whoosh':      ['sfx/melee/whoosh-1.ogg', 'sfx/melee/whoosh-2.ogg', 'sfx/melee/whoosh-3.ogg'],

  // Defense
  'def-dodge':       ['sfx/defense/dodge-1.ogg', 'sfx/defense/dodge-2.ogg'],
  'def-block':       'sfx/defense/block-shield.ogg',
  'def-parry':       'sfx/defense/parry-clash.ogg',
  'def-armor-hit':   'sfx/defense/armor-hit.ogg',
  'def-immune':      'sfx/defense/immune.ogg',

  // Footsteps (5 variants each)
  'foot-grass':   ['sfx/footsteps/grass-1.ogg','sfx/footsteps/grass-2.ogg','sfx/footsteps/grass-3.ogg','sfx/footsteps/grass-4.ogg','sfx/footsteps/grass-5.ogg'],
  'foot-stone':   ['sfx/footsteps/stone-1.ogg','sfx/footsteps/stone-2.ogg','sfx/footsteps/stone-3.ogg','sfx/footsteps/stone-4.ogg','sfx/footsteps/stone-5.ogg'],
  'foot-wood':    ['sfx/footsteps/wood-1.ogg','sfx/footsteps/wood-2.ogg','sfx/footsteps/wood-3.ogg','sfx/footsteps/wood-4.ogg','sfx/footsteps/wood-5.ogg'],
  'foot-snow':    ['sfx/footsteps/snow-1.ogg','sfx/footsteps/snow-2.ogg','sfx/footsteps/snow-3.ogg','sfx/footsteps/snow-4.ogg','sfx/footsteps/snow-5.ogg'],
  'foot-carpet':  ['sfx/footsteps/carpet-1.ogg','sfx/footsteps/carpet-2.ogg','sfx/footsteps/carpet-3.ogg','sfx/footsteps/carpet-4.ogg','sfx/footsteps/carpet-5.ogg'],

  // Equip
  'equip-draw-blade':    'sfx/equip/draw-blade.ogg',
  'equip-sheath-blade':  'sfx/equip/sheath-blade.ogg',
  'equip-cloth':         ['sfx/equip/cloth-1.ogg', 'sfx/equip/cloth-2.ogg'],
  'equip-leather':       ['sfx/equip/leather-1.ogg', 'sfx/equip/leather-2.ogg'],
  'equip-leather-belt':  'sfx/equip/leather-belt.ogg',
  'equip-leather-drop':  'sfx/equip/drop-leather.ogg',

  // Loot
  'loot-coins':           ['sfx/loot/coins-1.ogg', 'sfx/loot/coins-2.ogg'],
  'loot-pickup-item':     'sfx/loot/pickup-item.ogg',
  'loot-pickup-armor':    'sfx/loot/pickup-armor.ogg',
  'loot-pickup-weapon':   'sfx/loot/pickup-weapon.ogg',

  // World
  'world-door-open':    ['sfx/world/door-open-1.ogg', 'sfx/world/door-open-2.ogg'],
  'world-door-close':   ['sfx/world/door-close-1.ogg', 'sfx/world/door-close-2.ogg'],
  'world-book-open':    'sfx/world/book-open.ogg',
  'world-book-close':   'sfx/world/book-close.ogg',
  'world-book-flip':    ['sfx/world/book-flip-1.ogg', 'sfx/world/book-flip-2.ogg'],
  'world-creak':        'sfx/world/creak-1.ogg',
  'world-metal-click':  'sfx/world/metal-click.ogg',
  'world-chop':         'sfx/world/chop.ogg',

  // UI
  'ui-click':         ['sfx/ui/click-1.ogg', 'sfx/ui/click-2.ogg', 'sfx/ui/click-3.ogg'],
  'ui-back':          'sfx/ui/back.ogg',
  'ui-close':         'sfx/ui/close.ogg',
  'ui-close-window':  'sfx/ui/close-window.ogg',
  'ui-confirm':       'sfx/ui/confirm.ogg',
  'ui-error':         'sfx/ui/error.ogg',
  'ui-cant-do':       'sfx/ui/cant-do.ogg',
  'ui-hover':         ['sfx/ui/hover-1.ogg', 'sfx/ui/hover-2.ogg'],
  'ui-tab':           'sfx/ui/tab.ogg',

  // Notify
  'notify-quest-accept':    'sfx/notify/quest-accept.ogg',
  'notify-quest-complete':  'sfx/notify/quest-complete.ogg',
  'notify-level-up-bell':   'sfx/notify/level-up-bell.ogg',

  // Spell impacts (samples)
  'spell-frost-impact': 'sfx/spells/frost-impact.ogg',
  'spell-ice-shatter':  'sfx/spells/ice-shatter.ogg',
};

type MusicId = 'main_theme' | 'stormwind' | 'orgrimmar' | 'elwynn' | 'durotar' | 'combat' | 'boss' | 'tavern' | 'death';
type AmbientId = 'forest_birds' | 'city_crowd' | 'tavern_fire' | 'cave_drip' | 'wind_howl' | 'water_lap';

// R138: combat/boss используют CC0 wav от Juhani Junkala (Epic Boss Battle).
// Остальные зоны — fantasy_theme.mp3 (Joth, OpenGameArt CC0). Позже
// добавим уникальные треки на каждую зону.
const MUSIC_FILES: Record<MusicId, string> = {
  main_theme: 'music/main_theme.mp3',
  stormwind:  'music/stormwind.mp3',
  orgrimmar:  'music/orgrimmar.mp3',
  elwynn:     'music/elwynn.mp3',
  durotar:    'music/durotar.mp3',
  combat:     'music/combat.wav',
  boss:       'music/boss.wav',
  tavern:     'music/tavern.mp3',
  death:      'music/death.mp3',
};

// Sirus 1:1 zone ambience lives under /audio-sirus/ (site root, not the
// bundled audio/ folder) — resolveAudioUrl() handles the prefix.
const AMBIENT_FILES: Record<AmbientId, string> = {
  forest_birds: 'audio-sirus/Sound/Ambience/ZoneAmbience/ForestNormalDay.wav',
  city_crowd:   'ambient/city_crowd.mp3',
  tavern_fire:  'ambient/tavern_fire.mp3',
  cave_drip:    'ambient/cave_drip.mp3',
  wind_howl:    'ambient/wind_howl.mp3',
  water_lap:    'ambient/water_lap.mp3',
};

function resolveAudioUrl(path: string): string {
  return path.startsWith('audio-sirus/') ? `${import.meta.env.BASE_URL}${path}` : BASE + path;
}

// ─────────────────────────── Helper types ────────────────────────────────

export type WeaponSubtype = 'sword-1h' | 'sword-2h' | 'axe-1h' | 'axe-2h' | 'mace-1h' | 'mace-2h' | 'hammer-2h' | 'polearm' | 'dagger' | 'fist' | 'staff' | 'bow' | 'crossbow' | 'wand';
export type SpellSchool = 'fire' | 'frost' | 'holy' | 'shadow' | 'nature' | 'arcane' | 'lightning' | 'physical';
export type Surface = 'grass' | 'stone' | 'wood' | 'snow' | 'carpet';
export type Defense = 'dodge' | 'block' | 'parry' | 'immune' | 'miss';

class AudioManagerImpl {
  private sfxCache = new Map<string, Howl>();
  private musicCurrent: { id: MusicId; howl: Howl } | null = null;
  private ambientCurrent: { id: AmbientId; howl: Howl } | null = null;
  private muted = false;
  private volumes = { master: 1.0, music: 0.6, sfx: 0.8, ambient: 0.5 };

  constructor() {
    this.loadSettings();
    // R232q5f: bridge SettingsPanel volume sliders → live audio engine.
    if (typeof window !== 'undefined') {
      window.addEventListener('aiwari-settings-changed', (e) => {
        const s = (e as CustomEvent<{ settings: { masterVolume?: number; musicVolume?: number; sfxVolume?: number; masterMuted?: boolean; musicMuted?: boolean; sfxMuted?: boolean } }>).detail?.settings;
        if (!s) return;
        if (typeof s.masterVolume === 'number') {
          this.setMasterVolume(s.masterVolume);
          // R232q5cq: пробрасываем master в MusicEngine для HTMLAudio music tracks.
          void import('./MusicEngine.js').then(({ setMasterVolumeForMusic }) => setMasterVolumeForMusic(s.masterVolume!));
        }
        if (typeof s.musicVolume === 'number') {
          this.setMusicVolume(s.musicVolume);
          // R232q5cq: пробрасываем music volume в MusicEngine.
          void import('./MusicEngine.js').then(({ setMusicVolume }) => setMusicVolume(s.musicVolume!));
        }
        if (typeof s.sfxVolume === 'number') this.setSfxVolume(s.sfxVolume);
        // Mute states.
        if (typeof s.masterMuted === 'boolean') {
          if (s.masterMuted !== this.muted) this.toggleMute();
          // R232q5ct: master mute должен silence MusicEngine (HTMLAudio) тоже.
          // Если активна также musicMuted — без разницы; иначе принудительно
          // mute музыку при master mute и unmute при master unmute (если muск не муте.)
          void import('./MusicEngine.js').then(({ setMusicMuted }) => {
            const finalMute = s.masterMuted! || s.musicMuted === true;
            setMusicMuted(finalMute);
          });
        }
        // R232q5ck/ct: пробрасываем music/sfx mute в соответствующие движки.
        // Music mute = musicMuted ИЛИ masterMuted (любой выключен → музыка молчит).
        if (typeof s.musicMuted === 'boolean') {
          void import('./MusicEngine.js').then(({ setMusicMuted }) => {
            setMusicMuted(s.musicMuted! || (s.masterMuted === true));
          });
        }
        if (typeof s.sfxMuted === 'boolean') {
          void import('./SfxEngine.js').then(({ setSfxMuted }) => setSfxMuted(s.sfxMuted!));
        }
      });
    }
  }

  private loadSettings(): void {
    try {
      const v = JSON.parse(localStorage.getItem('aiwari.audio.volumes') || '{}');
      Object.assign(this.volumes, v);
      this.muted = localStorage.getItem('aiwari.audio.muted') === 'true';
      Howler.mute(this.muted);
    } catch { /* ignore */ }
  }
  private saveSettings(): void {
    localStorage.setItem('aiwari.audio.volumes', JSON.stringify(this.volumes));
    localStorage.setItem('aiwari.audio.muted', String(this.muted));
  }

  setMasterVolume(v: number): void {
    this.volumes.master = v;
    Howler.volume(v);
    // R232q5cr: applying master также trigger refresh on SFX (Howler.volume — глобально для Howler, но cache instances могут иметь свои volumes).
    for (const h of this.sfxCache.values()) {
      try { h.volume(Math.max(0, Math.min(1, this.volumes.sfx * v))); } catch { /* ignore */ }
    }
    this.saveSettings();
  }
  setMusicVolume(v: number): void { this.volumes.music = v; if (this.musicCurrent) this.musicCurrent.howl.volume(v * this.volumes.master); this.saveSettings(); }
  setSfxVolume(v: number): void {
    this.volumes.sfx = v;
    // R232q5cr: пробрасываем новую громкость на все cached Howl SFX (и music если попало в кэш).
    const newVol = v * this.volumes.master;
    for (const h of this.sfxCache.values()) {
      try { h.volume(Math.max(0, Math.min(1, newVol))); } catch { /* ignore broken howl */ }
    }
    this.saveSettings();
  }
  setAmbientVolume(v: number): void { this.volumes.ambient = v; if (this.ambientCurrent) this.ambientCurrent.howl.volume(v * this.volumes.master); this.saveSettings(); }
  toggleMute(): boolean { this.muted = !this.muted; Howler.mute(this.muted); this.saveSettings(); return this.muted; }
  isMuted(): boolean { return this.muted; }

  /** Lazy-load Howl per resolved path. Edgart arrays cache one Howl per file. */
  private getHowl(path: string, volScale = 1): Howl {
    let h = this.sfxCache.get(path);
    if (!h) {
      h = new Howl({
        src: [resolveAudioUrl(path)],
        volume: this.volumes.sfx * this.volumes.master * volScale,
        preload: true,
        onloaderror: () => { /* silent — file may not exist */ },
      });
      this.sfxCache.set(path, h);
    }
    return h;
  }

  // R232q5al: positional listener — game camera (or player) feeds the
  // listener world position so SFX with `position3d` get stereo pan + distance
  // attenuation. Howler manages a global listener via Howler.pos().
  private listenerPos: [number, number, number] = [0, 0, 0];
  private maxAudibleDistance = 60; // meters — beyond this, SFX is silent

  setListener(x: number, y: number, z: number, _forwardX = 0, _forwardY = 0, _forwardZ = 1): void {
    this.listenerPos = [x, y, z];
    try { Howler.pos(x, y, z); } catch { /* older Howler builds */ }
  }

  /** Compute stereo pan + volume scale for a world-space emitter. */
  private spatialise(pos: [number, number, number]): { pan: number; distScale: number } {
    const [lx, , lz] = this.listenerPos;
    const [px, , pz] = pos;
    const dx = px - lx;
    const dz = pz - lz;
    const dist = Math.hypot(dx, dz);
    if (dist >= this.maxAudibleDistance) return { pan: 0, distScale: 0 };
    // Linear pan: -1 (left) … +1 (right). Right = +x by convention.
    const pan = dist > 0.01 ? Math.max(-1, Math.min(1, dx / Math.max(1, this.maxAudibleDistance * 0.5))) : 0;
    // Inverse-square-ish falloff, capped.
    const distScale = Math.max(0, 1 - (dist / this.maxAudibleDistance));
    return { pan, distScale: distScale * distScale };
  }

  /** One-shot SFX. Plays a sample if mapped, else falls back to synth name if recognised.
   *  R232q5al: opts.position3d adds positional pan+attenuation when set. */
  playSfx(id: SfxId | SynthName, opts?: { volume?: number; rate?: number; position3d?: [number, number, number] }): void {
    if (this.muted) return;
    let volScale = opts?.volume ?? 1;
    let pan: number | null = null;
    if (opts?.position3d) {
      const sp = this.spatialise(opts.position3d);
      if (sp.distScale <= 0) return; // out of audible range
      volScale *= sp.distScale;
      pan = sp.pan;
    }
    const path = SFX_FILES[id as SfxId];
    if (path) {
      const resolved = Array.isArray(path) ? (path[Math.floor(Math.random() * path.length)] || path[0]!) : path;
      const h = this.getHowl(resolved, volScale);
      h.volume(this.volumes.sfx * this.volumes.master * volScale);
      if (opts?.rate) h.rate(opts.rate);
      const id2 = h.play();
      if (pan !== null) {
        try { h.stereo(pan, id2); } catch { /* mono-only fallback */ }
      }
      return;
    }
    // Fallback to synth
    try { playSynth(id as SynthName); } catch { /* unknown name — silent */ }
  }

  // ─── High-level helpers (the 80+ trigger surface) ───

  /** Auto-attack swing impact, weapon-aware. */
  playMelee(weapon: WeaponSubtype, hit = true): void {
    const map: Record<WeaponSubtype, SfxId> = {
      'sword-1h':  hit ? 'melee-sword-1h' : 'melee-whoosh',
      'sword-2h':  hit ? 'melee-sword-2h' : 'melee-whoosh',
      'axe-1h':    hit ? 'melee-axe-1h'   : 'melee-whoosh',
      'axe-2h':    hit ? 'melee-axe-2h'   : 'melee-whoosh',
      'mace-1h':   hit ? 'melee-mace-1h'  : 'melee-whoosh',
      'mace-2h':   hit ? 'melee-hammer-2h': 'melee-whoosh',
      'hammer-2h': hit ? 'melee-hammer-2h': 'melee-whoosh',
      'polearm':   hit ? 'melee-polearm'  : 'melee-whoosh',
      'dagger':    hit ? 'melee-dagger'   : 'melee-whoosh',
      'fist':      hit ? 'melee-fist-light': 'melee-whoosh',
      // Casters' weapons fall through to whoosh on melee
      'staff':     'melee-whoosh',
      'wand':      'melee-whoosh',
      'bow':       'melee-whoosh',
      'crossbow':  'melee-whoosh',
    };
    this.playSfx(map[weapon] ?? 'melee-whoosh');
  }

  playRanged(kind: 'bow-draw' | 'bow-shoot' | 'arrow-hit' | 'crossbow-shoot'): void {
    // We have legacy bow_draw/bow_release/arrow_impact MP3s — use them when present, fall back to synth.
    if (kind === 'bow-draw') this.playSfx('bow_draw');
    else if (kind === 'bow-shoot' || kind === 'crossbow-shoot') this.playSfx('bow_release');
    else if (kind === 'arrow-hit') this.playSfx('arrow_impact');
  }

  /** Cast start. School-aware — synthesised in SfxEngine if no sample. */
  playCast(school: SpellSchool): void {
    // Prefer existing legacy MP3 spell loops, else synth 'cast'/'heal'
    if (school === 'fire') this.playSfx('spell_fire');
    else if (school === 'frost') this.playSfx('spell_frost');
    else if (school === 'holy') this.playSfx('spell_holy');
    else if (school === 'shadow') this.playSfx('spell_shadow');
    else this.playSfx('cast' as SynthName);  // synth fallback for nature/arcane/lightning/physical
  }

  /** Spell impact / explosion. */
  playImpact(school: SpellSchool): void {
    if (school === 'frost') this.playSfx('spell-frost-impact');
    else if (school === 'fire') this.playSfx('spell_fire');
    else if (school === 'holy') this.playSfx('heal' as SynthName);
    else if (school === 'shadow') this.playSfx('spell_shadow');
    else this.playSfx('hit' as SynthName);
  }

  playFootstep(surface: Surface = 'grass'): void {
    const map: Record<Surface, SfxId> = {
      grass: 'foot-grass', stone: 'foot-stone', wood: 'foot-wood', snow: 'foot-snow', carpet: 'foot-carpet',
    };
    this.playSfx(map[surface], { volume: 0.55 });
  }

  playDefense(kind: Defense): void {
    const map: Record<Defense, SfxId | SynthName> = {
      dodge: 'def-dodge', block: 'def-block', parry: 'def-parry', immune: 'def-immune', miss: 'miss',
    };
    this.playSfx(map[kind] as SfxId);
  }

  playPain(severity: 'light' | 'heavy' | 'crit', gender: 'male' | 'female' = 'male'): void {
    if (severity === 'crit') this.playSfx(gender === 'female' ? 'damage_female' : 'damage_male', { rate: 1.2 });
    else this.playSfx(gender === 'female' ? 'damage_female' : 'damage_male', { volume: severity === 'heavy' ? 1.0 : 0.6 });
  }

  playDeath(gender: 'male' | 'female' = 'male'): void {
    this.playSfx(gender === 'female' ? 'death_female' : 'death_male');
  }

  playLoot(kind: 'coin' | 'item' | 'armor' | 'weapon'): void {
    if (kind === 'coin') this.playSfx('loot-coins');
    else if (kind === 'armor') this.playSfx('loot-pickup-armor');
    else if (kind === 'weapon') this.playSfx('loot-pickup-weapon');
    else this.playSfx('loot-pickup-item');
  }

  playEquip(slot: 'weapon' | 'armor' | 'cloth' | 'leather' | 'unequip'): void {
    if (slot === 'unequip') this.playSfx('equip-sheath-blade');
    else if (slot === 'weapon') this.playSfx('equip-draw-blade');
    else if (slot === 'cloth') this.playSfx('equip-cloth');
    else if (slot === 'leather') this.playSfx('equip-leather');
    else this.playSfx('equip_armor');
  }

  playUI(action: 'click' | 'hover' | 'tab' | 'back' | 'close' | 'confirm' | 'error' | 'cant-do'): void {
    const map = {
      click: 'ui-click', hover: 'ui-hover', tab: 'ui-tab', back: 'ui-back',
      close: 'ui-close', confirm: 'ui-confirm', error: 'ui-error', 'cant-do': 'ui-cant-do',
    } as const;
    this.playSfx(map[action] as SfxId);
  }

  playWorld(kind: 'door-open' | 'door-close' | 'book-open' | 'book-close' | 'book-flip' | 'creak' | 'chop' | 'metal-click'): void {
    const map = {
      'door-open': 'world-door-open', 'door-close': 'world-door-close',
      'book-open': 'world-book-open', 'book-close': 'world-book-close', 'book-flip': 'world-book-flip',
      creak: 'world-creak', chop: 'world-chop', 'metal-click': 'world-metal-click',
    } as const;
    this.playSfx(map[kind] as SfxId);
  }

  playNotify(kind: 'quest-accept' | 'quest-complete' | 'level-up'): void {
    if (kind === 'level-up') this.playSfx('notify-level-up-bell');
    else if (kind === 'quest-accept') this.playSfx('notify-quest-accept');
    else this.playSfx('notify-quest-complete');
  }

  // ─── Music / Ambient ───

  playMusic(id: MusicId | null): void {
    if (this.musicCurrent?.id === id) return;
    if (this.musicCurrent) {
      const old = this.musicCurrent.howl;
      old.fade(old.volume(), 0, 1500);
      setTimeout(() => old.unload(), 1600);
      this.musicCurrent = null;
    }
    if (!id) return;
    const howl = new Howl({
      src: [resolveAudioUrl(MUSIC_FILES[id])],
      loop: true, volume: 0, autoplay: true,
      onloaderror: () => { /* silent */ },
    });
    howl.fade(0, this.volumes.music * this.volumes.master, 1500);
    this.musicCurrent = { id, howl };
  }

  playAmbient(id: AmbientId | null): void {
    if (this.ambientCurrent?.id === id) return;
    if (this.ambientCurrent) {
      const old = this.ambientCurrent.howl;
      old.fade(old.volume(), 0, 1000);
      setTimeout(() => old.unload(), 1100);
      this.ambientCurrent = null;
    }
    if (!id) return;
    const howl = new Howl({
      src: [resolveAudioUrl(AMBIENT_FILES[id])],
      loop: true, volume: 0, autoplay: true,
      onloaderror: () => { /* silent */ },
    });
    howl.fade(0, this.volumes.ambient * this.volumes.master, 1000);
    this.ambientCurrent = { id, howl };
  }
}

export const audio = new AudioManagerImpl();
export type { MusicId, AmbientId };
